import type { NewGameMode } from '@brig/api-types';
import { DEFAULT_GAME_SIZE, type GameSizeId } from '@brig/rules-engine';
import { button, paper } from '@brig/theme/styles';
import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useCreateGame } from '../api/hooks.ts';
import { DEFAULT_COLOURS, DEFAULT_NATIONS, type Colours, type Nations } from '../game/history.ts';
import { SizePicker } from '../game/SizePicker.tsx';
import { FleetPicker } from './FleetPicker.tsx';
import * as styles from './NewGameButton.css.ts';

/** The computer first: a game at once, with nobody to wait for. */
const MODES: readonly NewGameMode[] = ['bot', 'online', 'hotseat'];

/**
 * The one way to start a game: "New Game" opens the choice of the game's size, of each fleet's
 * nation and colour, and of how to play — against the computer, with a friend online, or two
 * at one screen — and the chosen game opens.
 */
export function NewGameButton({ size = 'md' }: { size?: 'sm' | 'md' }) {
  const { t } = useTranslation();
  const titleId = useId();
  const dialog = useRef<HTMLDialogElement>(null);
  const navigate = useNavigate();
  const create = useCreateGame();
  const [gameSize, setGameSize] = useState<GameSizeId>(DEFAULT_GAME_SIZE);
  const [nations, setNations] = useState<Nations>(DEFAULT_NATIONS);
  const [colours, setColours] = useState<Colours>(DEFAULT_COLOURS);
  const start = (mode: NewGameMode) => {
    if (create.isPending) return;
    create.mutate(
      { mode, size: gameSize, nations, colours },
      {
        onSuccess: (game) => {
          dialog.current?.close();
          void navigate(`/games/${game.id}`);
        },
      },
    );
  };
  return (
    <>
      <button
        type="button"
        className={button({ tone: 'seal', size })}
        aria-haspopup="dialog"
        onClick={() => {
          create.reset();
          dialog.current?.showModal();
        }}
      >
        {t('nav.newGame')}
      </button>
      <dialog
        ref={dialog}
        className={`${paper} ${styles.dialog}`}
        aria-labelledby={titleId}
        onClick={(e) => {
          // A click on the backdrop (the dialog itself, outside its content) closes it.
          if (e.target === e.currentTarget) e.currentTarget.close();
        }}
      >
        <div className={styles.head}>
          <h2 id={titleId} className={styles.title}>
            {t('newGame.title')}
          </h2>
          <button
            type="button"
            className={styles.close}
            aria-label={t('newGame.close')}
            onClick={() => dialog.current?.close()}
          >
            ✕
          </button>
        </div>
        <SizePicker value={gameSize} onChange={setGameSize} />
        <div className={styles.section}>
          <FleetPicker nations={nations} onNations={setNations} colours={colours} onColours={setColours} />
        </div>
        <h3 className={styles.how}>{t('newGame.how')}</h3>
        <ul className={styles.choices}>
          {MODES.map((mode) => (
            <li key={mode}>
              <button
                type="button"
                className={styles.choice}
                aria-disabled={create.isPending}
                onClick={() => {
                  start(mode);
                }}
              >
                <span className={styles.choiceName}>{t(`newGame.${mode}.name`)}</span>
                <span className={styles.choiceHint}>{t(`newGame.${mode}.hint`)}</span>
              </button>
            </li>
          ))}
        </ul>
        {create.isError ? (
          <p className={styles.error} role="alert">
            {t('nav.newGameFailed')}
          </p>
        ) : null}
      </dialog>
    </>
  );
}
